// @ts-nocheck
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import {
  getHasFootfallAttribution,
  getHasInsightsPermissions,
  getHasReportingFootfallAttributionMapPermissions,
  getHasViewability,
  getSectionsVisibility,
  getSelectedCampaigns,
  getStaticMode
} from '@/store/selectors/reporting.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { SectionToggles } from '../SectionToggles';
import { ShortcutsStyle } from './shortcuts.style';
import { ShortcutConfig, shortcutsConfig } from './shortcutsConfig';

type State = {
  availableShortCuts: ShortcutConfig[];
  availableShortCutsFixed: Record<string, boolean>;
  sectionViewSaved: boolean;
  sectionViewToggles: {
    descriptionId: string;
    id: string;
    selected: boolean;
  }[];
  sectionsViewVisible: boolean;
};

function Shortcuts() {
  const { formatMessage } = useIntl();
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);
  const hasInsightsPermission = useSelector(getHasInsightsPermissions);
  const hasReportingFootfallAttributionMapsPermissions = useSelector(getHasReportingFootfallAttributionMapPermissions);
  const hasFootfallAttributionPermission = useSelector(getHasFootfallAttribution);
  const campaignsHaveViewability = useSelector(getHasViewability);
  const staticMode = useSelector(getStaticMode);
  const campaigns = useSelector(getSelectedCampaigns);
  const sectionsVisibility = useSelector(getSectionsVisibility);
  const isMulti = campaigns.length > 1;
  const hasFootfallAttributionTablePermission = checkUserResourcePermissions(
    'reportingFootfallAttributionTable',
    user,
    userRoles
  );
  const campaignsBottomPanelPermission = checkUserResourcePermissions('reportingHeaderBottomPanel', user, userRoles);
  const hasViewabilityPermission = checkUserResourcePermissions('reportingIasViewabilityEvents', user, userRoles);
  const sectionTogglesPermission = checkUserResourcePermissions(
    'reportingShortcutsSectionsVisibility',
    user,
    userRoles
  );
  const mapPermission = checkUserResourcePermissions('reportingMap', user, userRoles);

  const hasReportingCampaignInfoPermission = () => {
    return isMulti && campaignsBottomPanelPermission;
  };

  const hasFootfallAttributionMapsPermission = () => {
    return hasFootfallAttributionPermission && hasReportingFootfallAttributionMapsPermissions;
  };

  const hasFootfallAttributionStatsPermission = () => {
    return hasFootfallAttributionPermission && hasFootfallAttributionTablePermission;
  };

  const permissionsMap: Record<string, boolean> = {
    reportingCampaignInfo: hasReportingCampaignInfoPermission(),
    reportingFootfallAttributionMap: hasFootfallAttributionMapsPermission(),
    reportingFootfallAttributionTable: hasFootfallAttributionStatsPermission(),
    reportingInsights: hasInsightsPermission,
    reportingMap: mapPermission,
    reportingViewabilityTable: hasViewabilityPermission && campaignsHaveViewability
  };

  const shortcuts = shortcutsConfig.filter(({ backupNeeded, id: shortcutId, permissions }) => {
    return (!permissions || permissionsMap[shortcutId]) && (!staticMode || !backupNeeded);
  });
  const [
    { availableShortCuts, availableShortCutsFixed, sectionViewSaved, sectionViewToggles, sectionsViewVisible },
    setState
  ] = useState<State>({
    availableShortCuts: shortcuts,
    availableShortCutsFixed: shortcuts
      .filter(({ isFixed }) => isFixed)
      .reduce(
        (obj, val) => ({
          ...obj,
          [val.id]: true
        }),
        {}
      ),
    sectionViewSaved: true,
    sectionViewToggles: [],
    sectionsViewVisible: false
  });

  useEffect(() => {
    setState(prevState => ({
      ...prevState,
      sectionViewToggles: availableShortCuts
        .filter(({ id }) => !availableShortCutsFixed[id])
        .reduce(
          (acc, { descriptionId, id }) =>
            acc.concat({
              descriptionId,
              id,
              selected: !!sectionsVisibility[id]
            }),
          [] as State['sectionViewToggles']
        )
    }));
  }, [availableShortCuts, sectionsVisibility]);

  const scrollTo = (elementId: string) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const enabledAvailableShortCuts = useMemo(() => {
    return availableShortCuts.filter(
      shortcut => sectionsVisibility[shortcut.id] || availableShortCutsFixed[shortcut.id]
    );
  }, [availableShortCuts, sectionsVisibility]);

  const sectionViewButtonToggled = useCallback(() => {
    if (!sectionsViewVisible) {
      document.body.classList.add('reporting-view-section-opened');
    } else {
      document.body.classList.remove('reporting-view-section-opened');
    }
    setState(prevState => ({
      ...prevState,
      sectionsViewVisible: !sectionsViewVisible
    }));
  }, [sectionsViewVisible]);

  const onSectionsChanged = useCallback(
    sectionsStatuses => {
      setState(prevState => ({
        ...prevState,
        sectionViewSaved: false,
        sectionViewToggles: sectionViewToggles.map(sectionViewToggle => ({
          ...sectionViewToggle,
          selected: sectionsStatuses[sectionViewToggle.id]
        }))
      }));
    },
    [sectionViewToggles]
  );

  const onSaved = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      sectionViewSaved: true
    }));
  }, []);

  return (
    <ShortcutsStyle className={`reportingShortcuts ${sectionsViewVisible ? 'withSectionsView' : ''}`}>
      <div className="reportingShortcuts__container reportingHeader__wrapper">
        <div className="reportingShortcuts__links">
          {enabledAvailableShortCuts.map(shortcut => {
            return (
              <a
                className="reportingShortcuts__link"
                key={`reportingShortcuts_link_${shortcut.descriptionId}`}
                // biome-ignore lint/a11y/useValidAnchor: off
                onClick={() => scrollTo(shortcut.id)}
                type="link"
              >
                {formatMessage({ id: shortcut.descriptionId })}
              </a>
            );
          })}
        </div>
        {sectionTogglesPermission && (
          <div className="reportingShortcuts__sectionViewButtonContainer" onClick={sectionViewButtonToggled}>
            <label className="reportingShortcuts__sectionViewButtonContainer__label">
              {formatMessage({
                defaultMessage: 'Section view',
                id: 'reporting.shortcuts.sectionView.button'
              })}
            </label>
            <span
              className={`fas fa-${sectionsViewVisible ? 'minus' : 'plus'} reportingShortcuts__sectionViewButtonContainer__button`}
            />
          </div>
        )}
      </div>
      {sectionTogglesPermission && sectionsViewVisible && !isEmpty(sectionViewToggles) && (
        <SectionToggles
          onSaved={onSaved}
          onSectionsChanged={onSectionsChanged}
          saved={sectionViewSaved}
          sections={sectionViewToggles}
        />
      )}
    </ShortcutsStyle>
  );
}

export default Shortcuts;
